import clsx from 'clsx'
import Autoplay from 'embla-carousel-autoplay'
import { useCallback, ReactNode, useEffect, useState } from 'react'
import useEmblaCarousel, { EmblaOptionsType } from 'embla-carousel-react'

/* assets */
import { ReactComponent as ArrowIcon } from 'public/assets/icons/overview/circular-right.svg'

type PropType = {
  className?: string
  sliderClassName?: string
  options?: EmblaOptionsType
  autoScroll?: boolean
  slides: ReactNode[]
  navigationType?: 'dot' | 'arrow' | 'all'
  buttonConfig?: {
    prevButtonClassName?: string
    nextButtonClassName?: string
  }
  slideStyle?: React.CSSProperties
}

const AUTO_SCROLL_TIMER = 6000 // in ms

// TODO: pause carousel on visibility change = hidden
function Carousel(props: PropType): JSX.Element {
  const { options, slides, buttonConfig, autoScroll = false } = props

  const [carouselRef, carousel] = useEmblaCarousel(
    {
      draggable: true,
      loop: true,
      align: 'start',
      inViewThreshold: 0.5,
      breakpoints: {
        '(max-width: 640px)': { align: 'start' }
      },
      ...options
    },
    autoScroll ? [Autoplay({ delay: AUTO_SCROLL_TIMER })] : []
  )

  const { navigationType } = props
  const [isScrolling, setIsScrolling] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [totalSlides, setTotalSlides] = useState(0)

  const handleScroll = useCallback(() => {
    if (carousel) {
      setIsScrolling(false)
      setCurrentIndex(carousel.selectedScrollSnap())
    }
  }, [carousel])

  useEffect(() => {
    if (carousel) {
      carousel.on('scroll', handleScroll)
      setTotalSlides(carousel.slideNodes().length)

      return () => {
        carousel.off('scroll', handleScroll)
      }
    }
  }, [carousel, handleScroll])

  const scrollPrev = useCallback(() => {
    if (carousel && !isScrolling) {
      const current = currentIndex
      if (current > 0) {
        setIsScrolling(true)
        carousel.scrollPrev()
      }
    }
  }, [carousel, isScrolling, currentIndex])

  const scrollNext = useCallback(() => {
    if (carousel && !isScrolling) {
      const current = currentIndex

      if (current < totalSlides - 1) {
        setIsScrolling(true)
        carousel.scrollNext()
      } else {
        setIsScrolling(false)
      }
    }
  }, [carousel, isScrolling, currentIndex, totalSlides])

  const prevButtonOpacityClass =
    currentIndex === 0 ? 'opacity-40' : 'opacity-100'
  const nextButtonOpacityClass =
    currentIndex === totalSlides - 1 ? 'opacity-40' : 'opacity-100'

  return (
    <div className="relative">
      <div className="container">
        {navigationType === 'arrow' || navigationType === 'all' ? (
          <div className="flex gap-5 | mb-12">
            <button
              className={clsx('left-0', prevButtonOpacityClass)}
              onClick={() => scrollPrev()}
              type="button"
            >
              <ArrowIcon
                className={clsx(
                  'w-14 h-14 md:w-16 md:h-16 mx-auto rotate-180 text-white',
                  buttonConfig?.prevButtonClassName
                )}
              />
            </button>
            <button
              className={clsx('right-0', nextButtonOpacityClass)}
              onClick={() => scrollNext()}
            >
              <ArrowIcon
                className={clsx(
                  'w-14 h-14 md:w-16 md:h-16 mx-auto text-white',
                  buttonConfig?.nextButtonClassName
                )}
              />
            </button>
          </div>
        ) : null}
      </div>

      <div
        className={clsx('relative overflow-hidden', props.className)}
        ref={carouselRef}
      >
        <div className="flex gap-10">
          {slides.map((slide, index) => (
            <div
              className={clsx(
                'min-w-fit',
                `${index === slides.length - 1 ? 'first:ml-10' : 'ml-0'}`,
                props.sliderClassName
              )}
              key={index}
              style={{ ...props.slideStyle }}
            >
              {slide}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default Carousel
